import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { v4 as uuid } from 'uuid';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowId, GridRowModes, GridRowModesModel } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import DebugModal from '~/components/DebugModal/DebugModal';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';

import { getMinSpendProductTypeOptions, getProductTypeOptions } from '~/services/PromoService';

import getAllCurrencyCodes from '~/utils/getAllCurrencyCodes';

import { ProductMultiSelectEditCell } from './ProductMultiSelectEditCell';
import { NumberEditCell, getProductLabel } from './VoucherRewardEditCells';

type VoucherReward = definitions['VoucherRewardV1'];
type VoucherCondition = definitions['VoucherConditionV1'];

type Props = {
  rewards: VoucherReward[];
  conditions?: VoucherCondition[];
  onChange: (rewards: VoucherReward[]) => void;
  currencyCode?: string;
  disabled?: boolean;
  showAdvanceDescription?: boolean;
};

export default function VoucherRewards({
  rewards,
  conditions = [],
  onChange,
  currencyCode = 'AUD',
  showAdvanceDescription,
  disabled = false,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();
  const promoProductTypes = promoMeta?.promo_product_types ?? [];

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [newRewardIds, setNewRewardIds] = useState<string[]>([]);

  useEffect(() => {
    setNewRewardIds((prev) => prev.filter((id) => rewards.some((reward) => reward.id_voucher_reward === id)));
  }, [rewards]);

  const productLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    const options = getProductTypeOptions(promoProductTypes, []);
    options.forEach((opt) => map.set(opt.value, opt.label));
    return map;
  }, [promoProductTypes]);

  const minSpendProductLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    const options = getMinSpendProductTypeOptions(promoProductTypes, []);
    options.forEach((opt) => map.set(opt.value, opt.label));
    return map;
  }, [promoProductTypes]);

  const getDefaultProducts = useCallback(() => {
    const defaults = promoProductTypes.flatMap((category) => {
      const allProduct = category.products.find((product) => product.product_bk === 'ALL' && !product.dontShowInAdmin);
      if (!allProduct) {
        return [];
      }
      return [`${category.category_bk}-${allProduct.product_bk}` as VoucherReward['products'][number]];
    });
    return defaults;
  }, [promoProductTypes]);

  const handleAddReward = useCallback(() => {
    const newId = uuid();
    const fallbackReward = rewards[0];
    const defaultProducts =
      fallbackReward?.products && fallbackReward.products.length > 0 ? fallbackReward.products : getDefaultProducts();
    const defaultRewardAmount = Number.isFinite(fallbackReward?.reward_amount) ? fallbackReward?.reward_amount : 50;
    onChange([
      ...rewards,
      {
        id_voucher_reward: newId,
        reward_amount: defaultRewardAmount,
        products: defaultProducts,
        min_spend: 500,
        currency_code: currencyCode as VoucherReward['currency_code'],
        min_spend_products: fallbackReward?.min_spend_products ?? [],
      },
    ]);
    setNewRewardIds((prev) => [...prev, newId]);
    setRowModesModel((prev) => ({ ...prev, [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'reward_amount' } }));
  }, [getDefaultProducts, onChange, rewards]);

  const handleEditClick = useCallback((id: GridRowId) => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  }, []);

  const handleSaveClick = useCallback((id: GridRowId) => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  }, []);

  const handleCancelClick = useCallback(
    (id: GridRowId) => {
      if (newRewardIds.includes(String(id))) {
        onChange(rewards.filter((reward) => reward.id_voucher_reward !== id));
        setNewRewardIds((prev) => prev.filter((rewardId) => rewardId !== id));
      }
      setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View, ignoreModifications: true } }));
    },
    [newRewardIds, onChange, rewards],
  );

  const handleDeleteReward = useCallback(
    (rewardId: string) => {
      const next = rewards.filter((reward) => reward.id_voucher_reward !== rewardId);
      onChange(next);
    },
    [onChange, rewards],
  );

  const validateReward = useCallback(
    (reward: VoucherReward) => {
      if (!Number.isFinite(reward.reward_amount) || Number(reward.reward_amount) <= 0) {
        enqueueSnackbar('Reward amount must be greater than 0', { variant: 'error' });
        return false;
      }
      if (!reward.products || reward.products.length === 0) {
        enqueueSnackbar('At least one product must be selected for the reward', { variant: 'error' });
        return false;
      }
      return true;
    },
    [enqueueSnackbar],
  );

  const processRowUpdate = useCallback(
    (updatedRow: VoucherReward) => {
      if (!validateReward(updatedRow)) {
        throw new Error('Validation failed');
      }
      const next = rewards.map((reward) =>
        reward.id_voucher_reward === updatedRow.id_voucher_reward ? updatedRow : reward,
      );
      onChange(next);
      setNewRewardIds((prev) => prev.filter((id) => id !== updatedRow.id_voucher_reward));
      return updatedRow;
    },
    [onChange, rewards, validateReward],
  );

  const handleProcessRowUpdateError = useCallback(
    (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update reward', { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  const defaultColumnsProps: Partial<GridColDef<VoucherReward>> = {
    editable: true,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    flex: 1,
  };

  const columns = useMemo<Array<GridColDef<VoucherReward>>>(
    () => [
      {
        field: 'reward_amount',
        headerName: 'Reward Amount',
        minWidth: 140,
        renderEditCell: (params) => <NumberEditCell {...params} />,
        ...defaultColumnsProps,
      },
      {
        field: 'currency_code',
        headerName: 'Currency',
        minWidth: 120,
        renderCell: (params) => (params.row as VoucherReward).currency_code || currencyCode,
        renderEditCell: (params) => (
          <TextField
            select
            size="small"
            fullWidth
            value={params.value ?? ''}
            disabled={disabled}
            onChange={(event) => {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: event.target.value,
              });
            }}
          >
            {getAllCurrencyCodes().map((currencyCodeOption: string) => (
              <MenuItem key={currencyCodeOption} value={currencyCodeOption}>
                {currencyCodeOption}
              </MenuItem>
            ))}
          </TextField>
        ),
        ...defaultColumnsProps,
      },
      {
        field: 'products',
        headerName: 'Products',
        minWidth: 220,
        flex: 2,
        renderCell: (params) => {
          const values = params.row.products ?? [];
          if (!values.length) {
            return '-';
          }
          return values.map((value) => getProductLabel(value, productLabelMap)).join(', ');
        },
        renderEditCell: (params) => (
          <ProductMultiSelectEditCell
            {...params}
            disabled={false}
            promoProductTypes={promoProductTypes}
            multiSelectType="products"
          />
        ),
        ...defaultColumnsProps,
      },
      {
        field: 'min_spend',
        headerName: 'Min Spend',
        minWidth: 120,
        renderCell: (params) => (params.row.min_spend == null ? '-' : params.row.min_spend),
        renderEditCell: (params) => <NumberEditCell {...params} disabled={false} />,
        ...defaultColumnsProps,
      },
      {
        field: 'min_spend_products',
        headerName: 'Min Spend Products',
        minWidth: 220,
        flex: 2,
        renderCell: (params) => {
          const values = params.row.min_spend_products ?? [];
          if (!values.length) {
            return '-';
          }
          return values.map((value) => getProductLabel(value, minSpendProductLabelMap)).join(', ');
        },
        renderEditCell: (params) => (
          <ProductMultiSelectEditCell
            {...params}
            promoProductTypes={promoProductTypes}
            multiSelectType="min_spend_products"
          />
        ),
        ...defaultColumnsProps,
      },
      {
        field: 'conditions',
        headerName: 'Conditions',
        minWidth: 160,
        editable: false,
        renderCell: (params) => {
          const rewardId = params.row.id_voucher_reward;
          const applicableConditions = conditions.filter((condition) => {
            const rewardIds = condition.voucher_reward_ids ?? [];
            return rewardIds.length === 0 || rewardIds.includes(rewardId);
          });
          const debugData = Object.fromEntries(
            applicableConditions.map((condition) => {
              const key = `${condition.condition_type} ${condition.condition_apply_type} ${condition.condition_value}           [${condition.id_voucher_condition}]`;
              return [key, condition];
            }),
          );
          return (
            <DebugModal
              type="generic"
              title={`${applicableConditions.length} condition${applicableConditions.length !== 1 ? 's' : ''}`}
              data={debugData}
            />
          );
        },
        ...defaultColumnsProps,
      },
      {
        field: 'debug',
        headerName: 'Debug',
        editable: false,
        renderCell: (params) => <DebugModal type="generic" title="Debug" data={{ reward: params.row }} />,
        ...defaultColumnsProps,
      },
      {
        field: 'actions',
        headerName: '',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const isEditing = rowModesModel[params.id]?.mode === GridRowModes.Edit;
          return (
            <Stack direction="row" spacing={1}>
              {isEditing ? (
                <>
                  <IconButton
                    aria-label="Save reward"
                    size="small"
                    onClick={() => handleSaveClick(params.id)}
                    disabled={disabled}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Cancel reward edit"
                    size="small"
                    onClick={() => handleCancelClick(params.id)}
                    disabled={disabled}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton
                    aria-label="Edit reward"
                    size="small"
                    onClick={() => handleEditClick(params.id)}
                    disabled={disabled}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Delete reward"
                    size="small"
                    onClick={() => handleDeleteReward(params.row.id_voucher_reward)}
                    disabled={disabled}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
          );
        },
      },
    ],
    [
      conditions,
      disabled,
      handleCancelClick,
      handleDeleteReward,
      handleEditClick,
      handleSaveClick,
      minSpendProductLabelMap,
      productLabelMap,
      promoProductTypes,
      rowModesModel,
      currencyCode,
    ],
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        {showAdvanceDescription && (
          <Typography variant="body2" color="text.secondary">
            The amount the voucher is worth, and for which products
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddReward}
          disabled={disabled || isPromoMetaLoading}
        >
          Add reward
        </Button>
      </Stack>
      <DataGrid
        rows={rewards}
        columns={columns}
        getRowId={(row) => row.id_voucher_reward}
        disableRowSelectionOnClick
        hideFooter
        loading={isPromoMetaLoading}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        sx={{ backgroundColor: 'background.paper' }}
      />
    </Box>
  );
}
